import { type ReactNode, createContext, useContext } from 'react';
import { z } from 'zod';

import { useLocalStorage } from '../../../hooks/useLocalStorage';

/** Lets an app persist `TableFilters` preferences somewhere other than localStorage. */
export interface TableFiltersEnvironment {
  usePersistedNarrowMaxHeight: () => [number | null, (next: number | null) => void];
}

function useLocalNarrowMaxHeight(): [number | null, (next: number | null) => void] {
  const [value, setValue] = useLocalStorage<number | null>('tableFilters.narrowMaxHeight', null, {
    schema: z.number().nullable(),
  });
  return [value, setValue];
}

const TableFiltersEnvironmentContext = createContext<TableFiltersEnvironment>({
  usePersistedNarrowMaxHeight: useLocalNarrowMaxHeight,
});

export function TableFiltersEnvironmentProvider({
  value,
  children,
}: {
  value: TableFiltersEnvironment;
  children?: ReactNode;
}) {
  return (
    <TableFiltersEnvironmentContext.Provider value={value}>
      {children}
    </TableFiltersEnvironmentContext.Provider>
  );
}

export function useTableFiltersEnvironment(): TableFiltersEnvironment {
  return useContext(TableFiltersEnvironmentContext);
}
